import React, { useEffect, useState } from "react";
import { useProjects } from "../context/ProjectContext";
import api, { apiError } from "../lib/api";
import { NoProject } from "../components/EmptyState";
import CostCodeSelect from "../components/CostCodeSelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Save, CalendarDays, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Mobile-friendly field entry form.
export default function WeeklyEntry() {
  const { selected } = useProjects();
  const [codes, setCodes] = useState([]);
  const [week, setWeek] = useState("");
  const [rows, setRows] = useState({}); // code -> {installed, st, ot}
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selected) { setLoading(false); return; }
    setLoading(true);
    api.get(`/projects/${selected.id}/cost-codes`).then((r) => setCodes(r.data)).finally(() => setLoading(false));
  }, [selected]);

  if (!selected) return <NoProject />;
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B192C]" /></div>;

  const setVal = (code, k, v) => setRows((p) => ({ ...p, [code]: { ...(p[code] || {}), [k]: v } }));

  const submit = async () => {
    if (!week) { toast.error("Select a week-ending date"); return; }
    const entries = Object.entries(rows).filter(([, v]) => (v.installed || v.st || v.ot));
    if (entries.length === 0) { toast.error("Enter at least one cost code"); return; }
    setSaving(true);
    try {
      for (const [code, v] of entries) {
        await api.post(`/projects/${selected.id}/actuals`, {
          week_ending: week, cost_code: code,
          installed_qty: parseFloat(v.installed) || 0,
          st_hours: parseFloat(v.st) || 0, ot_hours: parseFloat(v.ot) || 0, notes: "",
        });
      }
      toast.success(`Saved ${entries.length} entries for ${week}`);
      setRows({});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-md mx-auto space-y-4" data-testid="weekly-entry-page">
      <h1 className="font-head text-2xl font-bold text-slate-900">Weekly Field Entry</h1>
      <p className="text-sm text-slate-500">Enter this period's installed quantity and hours. Increments only — not cumulative totals.</p>

      <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm">
        <Label className="text-xs flex items-center gap-1"><CalendarDays className="w-4 h-4" />Week Ending</Label>
        <Input type="date" value={week} onChange={(e) => setWeek(e.target.value)} className="mt-1" data-testid="weekly-date-input" />
      </div>

      <div className="space-y-3">
        {codes.map((c) => {
          const v = rows[c.cost_code] || {};
          const active = v.installed || v.st || v.ot;
          return (
            <div key={c.id} className={`bg-white border rounded-md p-3 shadow-sm ${active ? "border-[#E8791A]" : "border-slate-200"}`}
                 data-testid={`weekly-card-${c.cost_code}`}>
              <div className="flex items-center justify-between mb-2">
                <div><span className="font-mono font-medium">{c.cost_code}</span><span className="text-slate-500 text-sm ml-2">{c.description}</span></div>
                {active && <CheckCircle2 className="w-4 h-4 text-[#E8791A]" />}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <NumField label={`Installed (${c.uom || "qty"})`} value={v.installed || ""} onChange={(x) => setVal(c.cost_code, "installed", x)} testid={`weekly-installed-${c.cost_code}`} />
                <NumField label="ST Hrs" value={v.st || ""} onChange={(x) => setVal(c.cost_code, "st", x)} testid={`weekly-st-${c.cost_code}`} />
                <NumField label="OT Hrs" value={v.ot || ""} onChange={(x) => setVal(c.cost_code, "ot", x)} testid={`weekly-ot-${c.cost_code}`} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-3">
        <Button onClick={submit} disabled={saving} className="w-full bg-[#E8791A] hover:bg-[#D66A0F] text-white h-12 text-base shadow-lg" data-testid="weekly-submit-button">
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}Save Weekly Entries
        </Button>
      </div>
    </div>
  );
}

const NumField = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-[10px] text-slate-500">{label}</Label>
    <Input type="number" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)}
           className="mt-1 h-11 text-center font-mono" data-testid={testid} />
  </div>
);
